import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import MarkdownRenderer from "./MarkdownRenderer";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  ArrowRight,
  Sparkles,
  Zap,
  Clock,
  Globe,
  Youtube,
  MessageSquare,
  Chrome,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Star,
  TrendingUp,
} from "lucide-react";

const API_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const Home = ({ apiKey, onSaveApiKey }) => {
  const [videoUrl, setVideoUrl] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteSummary, setWebsiteSummary] = useState(null);
  const [websiteLoading, setWebsiteLoading] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [activeTab, setActiveTab] = useState("youtube");

  useEffect(() => {
    document.title = "RecapMate - AI-Powered Content Summarizer";
  }, []);

  const extractVideoId = (url) => {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  };

  const handleSummarize = async (e) => {
    e.preventDefault();

    if (!videoUrl) {
      setError("Please enter a YouTube video URL");
      return;
    }

    if (!apiKey) {
      setError(
        "You need an API key to summarize videos. Get one by purchasing credits."
      );
      return;
    }

    const videoId = extractVideoId(videoUrl);
    if (!videoId) {
      setError("Invalid YouTube URL. Please enter a valid URL.");
      return;
    }

    setLoading(true);
    setError(null);
    setSummary(null);

    try {
      const response = await axios.get(`${API_URL}/summarize/${videoId}`, {
        headers: { "X-API-Key": apiKey },
      });
      setSummary(response.data);

      if (window.gtag) {
        window.gtag("event", "generate_summary", {
          video_id: videoId,
        });
      }
    } catch (err) {
      if (err.response?.status === 402 || err.response?.data?.quotaExceeded) {
        setError(
          "You have run out of credits. Please purchase more credits to continue."
        );
      } else if (err.response?.data?.isAgeRestricted) {
        setError(
          "This video is age-restricted and cannot be summarized via the web app. Try our Chrome extension for age-restricted videos."
        );
      } else if (err.response?.status === 403) {
        setError(
          "Invalid API key. Please check your API key and try again."
        );
      } else if (err.response?.status === 429) {
        setError(
          "Too many requests. Please wait a moment and try again."
        );
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Failed to summarize video. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = (e) => {
    e.preventDefault();
    if (apiKeyInput) {
      onSaveApiKey(apiKeyInput);
      setApiKeyInput("");
    } else {
      setError("Please enter an API key");
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleWebsiteSummarize = async (e) => {
    e.preventDefault();

    if (!websiteUrl) {
      setError("Please enter a website URL");
      return;
    }

    if (!apiKey) {
      setError(
        "You need an API key to summarize websites. Get one by purchasing credits."
      );
      return;
    }

    if (!isValidUrl(websiteUrl)) {
      setError("Invalid URL. Please enter a valid website URL.");
      return;
    }

    setWebsiteLoading(true);
    setError(null);
    setWebsiteSummary(null);

    try {
      const response = await axios.post(
        `${API_URL}/summarize-website`,
        {
          url: websiteUrl,
          summaryType: "website-standard",
        },
        {
          headers: {
            "X-API-Key": apiKey,
            "Content-Type": "application/json",
          },
        }
      );
      setWebsiteSummary(response.data);

      if (window.gtag) {
        window.gtag("event", "generate_website_summary", {
          website_url: websiteUrl,
        });
      }
    } catch (err) {
      if (err.response?.status === 402) {
        setError(
          "You have run out of credits. Please purchase more credits to continue."
        );
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Failed to summarize website. Please try again later.");
      }
    } finally {
      setWebsiteLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>RecapMate - AI-Powered Content Summarizer</title>
        <meta
          name="description"
          content="Save time with RecapMate - the AI-powered summarizer for YouTube videos and websites. Get key points from any content in seconds."
        />
        <meta
          name="keywords"
          content="YouTube summarizer, website summarizer, video summary, AI summary tool, save time, content analysis"
        />
      </Helmet>

      <div className="relative">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-subtle">
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none"></div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center px-4 py-2 mb-6 rounded-full bg-primary/10 text-primary text-sm font-medium animate-in fade-in slide-in duration-500">
                <Sparkles className="w-4 h-4 mr-2" />
                AI-Powered Summarization
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 tracking-tight animate-in fade-in slide-in duration-700">
                Save Hours with
                <br />
                <span className="gradient-text">Smart Summaries</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto animate-in fade-in slide-in duration-900">
                Get instant, AI-generated summaries of YouTube videos and websites. 
                Perfect for researchers, students, and professionals.
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12 animate-in fade-in slide-in duration-1100">
                <Button 
                  asChild 
                  size="lg" 
                  className="gradient-primary text-white border-0 hover:opacity-90 transition-all hover-lift"
                >
                  <Link to="/buy-credits">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  variant="outline" 
                  className="hover-lift"
                >
                  <a
                    href="https://t.me/recapmate_bot"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Try on Telegram
                  </a>
                </Button>
              </div>

              {/* Platform badges */}
              <div className="flex flex-wrap justify-center gap-4 animate-in fade-in slide-in duration-1300">
                <div className="flex items-center px-4 py-2 rounded-full bg-muted/50 backdrop-blur-sm">
                  <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-sm">Web App</span>
                </div>
                <div className="flex items-center px-4 py-2 rounded-full bg-muted/50 backdrop-blur-sm">
                  <MessageSquare className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-sm">Telegram Bot</span>
                </div>
                <div className="flex items-center px-4 py-2 rounded-full bg-muted/50 backdrop-blur-sm">
                  <Chrome className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-sm">Chrome Extension</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Summarizer Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              {!apiKey ? (
                <Card className="shadow-xl animate-in fade-in slide-in duration-500">
                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl">Enter Your API Key</CardTitle>
                    <p className="text-muted-foreground mt-2">
                      Start summarizing content in seconds
                    </p>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSaveKey} className="space-y-4">
                      <Input
                        type="text"
                        value={apiKeyInput}
                        onChange={(e) => setApiKeyInput(e.target.value)}
                        placeholder="Your API key"
                        className="h-12 text-base focus-ring"
                      />
                      <Button type="submit" className="w-full h-12 text-base gradient-primary text-white border-0">
                        Save API Key
                      </Button>
                    </form>
                    <div className="mt-6 text-center">
                      <p className="text-muted-foreground">
                        Don't have an API key?{" "}
                        <Link
                          to="/buy-credits"
                          className="text-primary hover:underline font-medium"
                        >
                          Get one here
                        </Link>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="shadow-xl animate-in fade-in slide-in duration-500">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl text-center">
                      Summarize Content Instantly
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-2 h-12">
                        <TabsTrigger value="youtube" className="flex items-center gap-2 text-base">
                          <Youtube className="h-4 w-4" />
                          YouTube
                        </TabsTrigger>
                        <TabsTrigger value="website" className="flex items-center gap-2 text-base">
                          <Globe className="h-4 w-4" />
                          Website
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="youtube" className="mt-6">
                        <form onSubmit={handleSummarize} className="space-y-4">
                          <Input
                            type="text"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            placeholder="https://youtube.com/watch?v=..."
                            className="h-12 text-base focus-ring"
                          />
                          <Button
                            type="submit"
                            className="w-full h-12 text-base"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Summarizing...
                              </>
                            ) : (
                              <>
                                <Sparkles className="mr-2 h-4 w-4" />
                                Summarize Video
                              </>
                            )}
                          </Button>
                        </form>
                      </TabsContent>
                      
                      <TabsContent value="website" className="mt-6">
                        <form onSubmit={handleWebsiteSummarize} className="space-y-4">
                          <Input
                            type="text"
                            value={websiteUrl}
                            onChange={(e) => setWebsiteUrl(e.target.value)}
                            placeholder="https://example.com"
                            className="h-12 text-base focus-ring"
                          />
                          <Button
                            type="submit"
                            className="w-full h-12 text-base"
                            disabled={websiteLoading}
                          >
                            {websiteLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Summarizing...
                              </>
                            ) : (
                              <>
                                <Sparkles className="mr-2 h-4 w-4" />
                                Summarize Website
                              </>
                            )}
                          </Button>
                        </form>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}

              {error && (
                <Alert variant="destructive" className="mt-6 animate-in fade-in slide-in">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {(summary || websiteSummary) && (
                <Card className="mt-8 shadow-xl animate-in fade-in slide-in">
                  <CardHeader className="pb-4">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-xl">
                        {summary ? summary.title : websiteSummary.title || "Summary"}
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(summary ? summary.summary : websiteSummary.summary)}
                        className="hover-lift"
                      >
                        {copiedText ? (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <MarkdownRenderer 
                      content={summary ? summary.summary : websiteSummary.summary}
                      variant="default"
                      className="prose prose-sm max-w-none"
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                Why Choose <span className="gradient-text">RecapMate</span>?
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Powerful AI technology designed to save you time and boost productivity
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6 hover-lift card-hover">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Lightning Fast</h3>
                <p className="text-muted-foreground">
                  Get summaries in seconds, not hours. Save valuable time every day.
                </p>
              </Card>

              <Card className="text-center p-6 hover-lift card-hover">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                  <Star className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">AI-Powered</h3>
                <p className="text-muted-foreground">
                  Advanced Gemini AI ensures accurate and comprehensive summaries.
                </p>
              </Card>

              <Card className="text-center p-6 hover-lift card-hover">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <Globe className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Multi-Platform</h3>
                <p className="text-muted-foreground">
                  Access via web, Telegram bot, or Chrome extension anywhere.
                </p>
              </Card>

              <Card className="text-center p-6 hover-lift card-hover">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                  <TrendingUp className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Boost Learning</h3>
                <p className="text-muted-foreground">
                  Perfect for students, researchers, and lifelong learners.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-primary text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Save Time?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              Join thousands of users who are transforming how they consume content.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                asChild 
                size="lg" 
                variant="secondary" 
                className="hover-lift"
              >
                <Link to="/buy-credits">
                  Get Started Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover-lift"
              >
                <a
                  href="https://chromewebstore.google.com/detail/recap-mate/ennlbhdomajblncebmknjapmonodfcdb"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Chrome className="mr-2 h-4 w-4" />
                  Install Extension
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;